import { definitionWithComparison } from './features/comparison';
import { MEASURE_COLUMN_NAME, TIMESTAMP_COLUMN_NAME } from './features/metricValue';
import { definitionWithStatus, SimpleStatus } from './features/status';
import { definitionWithStaticTarget } from './features/threshold';
import { DateWindow, definitionWithWindow } from './features/window';
import { definitionWithWarehousing } from './features/warehousing';
import {
  EffectiveDefinition,
  DateTrunc,
  DateGranularity,
  makeHistoricalComparisonSpec,
  makeMarkBasedComparisonSpec,
} from './models';

export class DefinitionBuilder {
  private def: EffectiveDefinition;

  public constructor(def: EffectiveDefinition) {
    this.def = def;
    return this;
  }

  public build = (): EffectiveDefinition => {
    return this.def;
  };

  public withHistoricalComparison = (
    dateTrunc: DateTrunc = DateTrunc.DAY,
    numberOfPeriods: number = 1,
    hideComparisonLine: boolean = false
  ): DefinitionBuilder => {
    const comparisonSpec = makeHistoricalComparisonSpec(dateTrunc, '', numberOfPeriods, !hideComparisonLine);

    this.def = definitionWithComparison(this.def, comparisonSpec, hideComparisonLine);
    return this;
  };

  public withMarkBasedComparison = (numberOfMarks: number = 1): DefinitionBuilder => {
    const comparisonSpec = makeMarkBasedComparisonSpec('', numberOfMarks);

    this.def = definitionWithComparison(this.def, comparisonSpec, false);
    return this;
  };

  public withStaticTarget = (constant: number): DefinitionBuilder => {
    this.def = definitionWithStaticTarget(this.def, constant);
    return this;
  };

  public withStatus = (status: SimpleStatus): DefinitionBuilder => {
    this.def = definitionWithStatus(this.def, status).unwrap();
    return this;
  };

  public withDateWindow = (window: DateWindow): DefinitionBuilder => {
    this.def = definitionWithWindow(this.def, window);
    return this;
  };

  public withWarehousing = (): DefinitionBuilder => {
    this.def = definitionWithWarehousing(this.def);
    return this;
  };
}

export const simpleDefinition = (
  dateGranularity: DateGranularity = DateGranularity.DAY,
  measureFormat?: string,
  isTimeless?: boolean,
  isSnapshotTimestamp?: boolean
): DefinitionBuilder => {
  return new DefinitionBuilder({
    schema: {
      measure: {
        name: MEASURE_COLUMN_NAME,
        type: 'number',
        format: measureFormat,
      },
      timestamp: {
        name: TIMESTAMP_COLUMN_NAME,
        type: 'datetime',
        isTimeless,
        isSnapshotTimestamp,
        dateGranularity,
      },
    },
    features: {
      measure: { column: MEASURE_COLUMN_NAME },
      timestamp: { column: TIMESTAMP_COLUMN_NAME },
    },
  });
};
